import { useState, useMemo, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';

import Icon from '../../../controls/Icon';
import AddNewKeyModal from './modals/AddNewKeyModal';
import { useUIConfig } from './Context';
import PubSub from '../../../../services/PubSub';
import BUILD_TIME_CONFIG_TYPES from '../../../../config/buildTimeConfigTypes';

const PropsInfo = ({
    jsx,
    withinBlockName,
    withinLayoutItemName,
    allowCreateArgs,
    maximizeButton,
    addKeyCallback,
}) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [hasHighligher, setHasHighlighter] = useState('');
    const [translate] = useTranslation();

    const { objectSubType, objectSubTypeId } = useParams();
    const history = useHistory();

    const { propEditorPath, navigateToLayout, checkLayoutItemUsage, selectedLayoutItem, selectedLayoutItemType, applyHighlighter, drillDownLayoutItem, drillDownLayoutItemType, canSendUpdate, saveChanges, applyToLivePreview, reservedArgs, blocks } = useUIConfig();

    const onCloseModal = useCallback(() => {
        setShowAddModal(false);
    }, []);

    const onAddKey = useCallback((key) => {
        if (reservedArgs.includes(key)) {
            PubSub.publish('show_info', translate('OTM_CANNOT_USE_RESERVED_KEY'));
            return;
        }
        addKeyCallback(key, '');

        setShowAddModal(false);
    }, [reservedArgs, addKeyCallback, translate]);

    const infoIcon = useMemo(() => {
        return (
            <div className='bg-info col-1 d-flex'>
                <Icon icon='InfoCircle' className='text-light fs-2 m-auto' />
            </div>
        );
    }, []);

    const showUsageLabel = useMemo(() => {
        if (selectedLayoutItemType === BUILD_TIME_CONFIG_TYPES.COMPONENTS && objectSubType !== BUILD_TIME_CONFIG_TYPES.COMPONENTS) {
            return false;
        }
        if (drillDownLayoutItemType === BUILD_TIME_CONFIG_TYPES.BLOCKS && selectedLayoutItemType !== BUILD_TIME_CONFIG_TYPES.BLOCKS) {
            return false;
        }
        if (selectedLayoutItemType === BUILD_TIME_CONFIG_TYPES.APPLETS && objectSubType !== BUILD_TIME_CONFIG_TYPES.APPLETS) {
            return false;
        }

        return selectedLayoutItem === objectSubTypeId;
    }, [selectedLayoutItem, selectedLayoutItemType, drillDownLayoutItemType, objectSubType, objectSubTypeId]);

    const usageLabel = useMemo(() => {
        if (showUsageLabel) {
            const { pages, applets, blocks, components } = checkLayoutItemUsage(objectSubType, objectSubTypeId);
            const message = [objectSubTypeId];

            const layoutItemsMessage = [];

            if (pages > 0) {
                layoutItemsMessage.push(`${pages} ${translate(pages === 1 ? 'OTM_PAGE' : 'OTM_PAGES')}`);
            }

            if (applets > 0) {
                layoutItemsMessage.push(`${applets} ${translate(applets === 1 ? 'OTM_APPLET' : 'OTM_APPLETS')}`);
            }

            if (blocks > 0) {
                layoutItemsMessage.push(`${blocks} ${translate(blocks === 1 ? 'OTM_BLOCK' : 'OTM_BLOCKS')}`);
            }

            if (components > 0) {
                layoutItemsMessage.push(`${components} ${translate(components === 1 ? 'OTM_COMPONENT' : 'OTM_COMPONENTS')}`);
            }

            if (layoutItemsMessage.length) {
                message.push(` ${translate('OTM_USED_BY')} ${layoutItemsMessage.join(', ')}`);
            }
            else {
                message.push(` ${translate('OTM_NOT_USED')}`);
            }

            return (
                <div className='border border-2 border-info row m-0 fs-7'>
                    {infoIcon}
                    <div className='col-11 py-1 mx-0'>
                        <p className="m-0">{message.join('')}</p>
                    </div>
                </div>
            );
        }

        return null;
    }, [checkLayoutItemUsage, objectSubType, objectSubTypeId, showUsageLabel, infoIcon, translate]);

    const blockNavigateButtons = useMemo(() => {
        if (withinBlockName) {
            return (
                <div className='col-auto d-flex flex-column justify-content-around'>
                    <Button
                        variant='primary'
                        className='btn-sm ms-auto me-1'
                        onClick={() => {
                            navigateToLayout({ history, layoutType: BUILD_TIME_CONFIG_TYPES.BLOCKS, layoutName: withinBlockName, state: { keepBcContext: true } });
                        }}
                    >
                        {translate('OTM_BLOCK_EDIT')}
                    </Button>

                    <div className="row">
                        <div className="col-6">
                            <Button
                                variant='primary'
                                className='btn-sm'
                                onClick={() => {
                                    navigateToLayout({ history, layoutType: BUILD_TIME_CONFIG_TYPES.BLOCKS, query: `create=true` });
                                }}
                            >
                                {translate('OTM_BLOCK_CREATE')}
                            </Button>
                        </div>
                        <div className="col-6">
                            <Button
                                variant='primary'
                                className='btn-sm'
                                onClick={() => {
                                    navigateToLayout({ history, layoutType: BUILD_TIME_CONFIG_TYPES.BLOCKS, query: `create=true`, state: { preselectedCopyItem: drillDownLayoutItem } });
                                }}
                            >
                                {translate('OTM_COPY')}
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    }, [withinBlockName, history, navigateToLayout, drillDownLayoutItem, translate]);

    const blockUsageLabel = useMemo(() => {
        const { pages: pagesCount, applets: appletsCount, blocks: blocksCount } = checkLayoutItemUsage(BUILD_TIME_CONFIG_TYPES.BLOCKS, withinBlockName);
        const message = [];

        const layoutItemsMessage = [];
        if (pagesCount + appletsCount + blocksCount === 1) {
            let layoutItemType = pagesCount ? 'OTM_PAGE' : (appletsCount ? 'OTM_APPLET' : 'OTM_BLOCK');

            layoutItemsMessage.push(` ${translate('OTM_BLOCK_ONLY_USE_CASE')} ${translate(layoutItemType)}`);
        }
        else {
            if (pagesCount > 0) {
                layoutItemsMessage.push(`${pagesCount} ${translate(pagesCount === 1 ? 'OTM_PAGE' : 'OTM_PAGES')}`);
            }

            if (appletsCount > 0) {
                layoutItemsMessage.push(`${appletsCount} ${translate(appletsCount === 1 ? 'OTM_APPLET' : 'OTM_APPLETS')}`);
            }

            if (blocksCount > 0) {
                layoutItemsMessage.push(`${blocksCount} ${translate(blocksCount === 1 ? 'OTM_BLOCK' : 'OTM_BLOCKS')}`);
            }

            if (layoutItemsMessage.length) {
                layoutItemsMessage[0] = `${translate('OTM_USED_BY')} ${layoutItemsMessage[0]}`;
            }
        }

        if (layoutItemsMessage.length) {
            const blockName = blocks[drillDownLayoutItem] ? drillDownLayoutItem : (blocks[withinLayoutItemName] ? withinLayoutItemName : withinBlockName);

            if (drillDownLayoutItem !== blockName) {
                message.push(`${drillDownLayoutItem} ${translate('OTM_BLOCK_WITHIN')} ${blockName}, ${layoutItemsMessage.join(', ')}`);
            }
            else {
                message.push(`${withinBlockName} ${layoutItemsMessage.join(', ')}`);
            }
        }

        if (message.length) {
            return (
                <p className="m-0">{message.join('')}</p>
            );
        }

        return null;
    }, [withinBlockName, withinLayoutItemName, drillDownLayoutItem, checkLayoutItemUsage, blocks, translate]);

    const blockInfo = useMemo(() => {
        if (withinBlockName) {
            return (
                <div className='border border-2 border-info row m-0 fs-7'>
                    {infoIcon}
                    <div className='col py-1 mx-0'>
                        <p className="m-0">{translate('OTM_BLOCK_READONLY_INFO')}</p>
                        {blockUsageLabel}
                        <p className="m-0">{translate('OTM_BLOCK_READONLY_OPTIONS_LABEL')}</p>
                        <ul className='m-0'>
                            <li>{translate('OTM_BLOCK_READONLY_OPTION1')}</li>
                            <li>{translate('OTM_BLOCK_READONLY_OPTION2')}</li>
                        </ul>
                    </div>
                    {blockNavigateButtons}
                </div>
            );
        }

        return null;
    }, [withinBlockName, infoIcon, blockUsageLabel, blockNavigateButtons, translate]);

    const highlightItemButton = useMemo(() => {
        return (
            <div className="col-auto ms-auto">
                <Button
                    onClick={() => {
                        if (hasHighligher === drillDownLayoutItem) {
                            applyToLivePreview();
                            setHasHighlighter('');
                        }
                        else {
                            applyHighlighter({ jsxName: jsx, editingPath: propEditorPath });
                            setHasHighlighter(drillDownLayoutItem);
                        }
                    }}
                    className='btn-sm'
                >
                    {hasHighligher === drillDownLayoutItem ? translate('OTM_CLEAR_HIGHLIGHT') : translate('OTM_HIGHLIGHT')}
                </Button>
            </div>
        );
    }, [propEditorPath, jsx, hasHighligher, applyHighlighter, applyToLivePreview, drillDownLayoutItem, translate]);

    const addCustomKeyButton = useMemo(() => {
        if (!withinBlockName && allowCreateArgs) {
            return (
                <div className='col-auto ms-1'>
                    <Button variant='primary' className='btn-sm' onClick={() => setShowAddModal(true)}>
                        {translate('OTM_ADD_KEY')}
                    </Button>
                </div>
            );
        }

        return null;
    }, [withinBlockName, allowCreateArgs, translate]);

    const applyLivePreviewButton = useMemo(() => {
        if (!withinBlockName) {
            return (
                <div className='col-auto ms-1'>
                    <Button
                        disabled={!canSendUpdate}
                        onClick={() => {
                            saveChanges();
                            applyToLivePreview();
                        }}
                        variant="outline-primary"
                        className='btn-sm border-0'
                    >
                        <Icon icon="Send" />
                    </Button>
                </div>
            );
        }

        return null;
    }, [withinBlockName, canSendUpdate, applyToLivePreview, saveChanges]);

    const headerText = useMemo(() => {
        const jsxComponentNames = [];

        if (withinLayoutItemName && withinLayoutItemName !== drillDownLayoutItem && withinLayoutItemName !== jsx) {
            jsxComponentNames.push(withinLayoutItemName);
        }

        jsxComponentNames.push(jsx);

        const jsxComponentName = jsxComponentNames.join(', ');

        return <div className="col">{drillDownLayoutItem} [{jsxComponentName}]</div>;
    }, [drillDownLayoutItem, withinLayoutItemName, jsx]);

    const renderMaximizeButton = useMemo(() => {
        if (!maximizeButton) {
            return;
        }

        return <div className='col-auto'>
            <button
                className="btn btn-sm btn-outline-primary p-0 border-0"
                type="button"
                onClick={maximizeButton.onClick}
            >
                <Icon icon={maximizeButton.icon} className="d-block" />
            </button>
        </div>
    }, [maximizeButton]);

    return (
        <>
            <div className='bg-light sticky-top mb-2' style={{ wordBreak: 'break-word' }}>
                <div className="rowHeading pb-1 sticky-top mb-3 h3">
                    {renderMaximizeButton}
                    {headerText}
                    {highlightItemButton}
                    {addCustomKeyButton}
                    {applyLivePreviewButton}
                </div>
            </div>
            {blockInfo}
            {usageLabel}
            <AddNewKeyModal
                show={showAddModal}
                onClose={onCloseModal}
                onAdd={onAddKey}
            />
        </>
    );
};

export default PropsInfo;